import {
  GradeCategory,
  type GradeCategoryWithLessons,
} from "@core/grades/types";
import { URLS } from "@core/shared/utils";
import { BaseLink } from "@design-system/components/link";
import { trans } from "@mongez/localization";
import { FolderIcon } from "lucide-react";
import { SectionHeading } from "../../home/components/SectionHeading";

type GradeCategoriesSectionProps = {
  categories: GradeCategory[];
  className?: string;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
};

export function GradeCategoriesSection({
  categories,
  className,
  title,
  description,
  icon = <FolderIcon className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 dark:text-primary-400" />,
}: GradeCategoriesSectionProps) {
  if (!categories?.length) {
    return (
      <div className={`py-8 text-center ${className}`}>
        <FolderIcon className="mx-auto mb-4 h-12 w-12 text-gray-400 dark:text-gray-500" />
        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {trans("grades.noCategoriesAvailable")}
        </h3>
        <p className="text-gray-600 dark:text-gray-300">
          {trans("grades.noCategoriesAvailableMessage")}
        </p>
      </div>
    );
  }

  return (
    <section className={className}>
      <SectionHeading title={title || trans("grades.categories")} icon={icon} />
      {description && (
        <p className="mb-6 text-gray-600 dark:text-gray-300">
          {description}
        </p>
      )}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {categories.map((category) => (
          <CategoryCard key={category.id} category={category} />
        ))}
      </div>
    </section>
  );
}

function CategoryCard({ category }: { category: GradeCategory }) {
  const { name, image, total } = category;

  return (
    <BaseLink
      to={URLS.grades.viewCategory(category)}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 transition-all duration-300 hover:scale-[1.025] hover:shadow-lg"
    >
      {/* Image Section */}
      <div className="relative aspect-video w-full overflow-hidden bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-gray-700 dark:to-gray-600">
        {image?.url ? (
          <img
            src={image.url}
            alt={name}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <FolderIcon className="h-16 w-16 text-primary-400 dark:text-primary-300" />
          </div>
        )}

        {/* Lessons count overlay */}
        <div className="absolute bottom-2 right-2 rounded-full bg-black/75 dark:bg-gray-900/90 px-3 py-1 text-xs font-medium text-white dark:text-gray-100 shadow-lg dark:shadow-xl border border-white/10 dark:border-gray-600/30">
          {trans("grades.totalLessons", {
            count: total?.lessons,
          })}
        </div>
      </div>

      {/* Content Section */}
      <div className="flex flex-1 flex-col p-4">
        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors group-hover:text-primary-600 dark:group-hover:text-primary-400">
          {name}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {trans("grades.lessonsInCategory", { count: total?.lessons })}
        </p>
      </div>
    </BaseLink>
  );
}
